// Add an engagement to an EXISTING person (multi-entity workforce Phase 3):
// company → relationship → terms → review → save as draft or submit for
// approval. The person is never re-created and never matched by name/email —
// the wizard is opened from their profile. Pay is not set here: an employment's
// salary is set in Payroll after approval; a contractor's fee in Contractor &
// External Pay.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/features/shared/components/ui/radio-group';
import { getApiErrorMessage } from '@/lib/apiError';
import { engagementsApi, type EngagementCompany } from './engagementsApi';
import {
  EMPTY_WIZARD, RELATIONSHIP_LABEL, WIZARD_STEPS, wizardErrors, wizardPayload,
  type WizardForm, type WizardStep,
} from './engagementRules';

interface Props {
  open: boolean;
  employeeId: string;
  personName: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const STEP_TITLE: Record<WizardStep, string> = {
  company: 'Company',
  relationship: 'Relationship',
  terms: 'Terms',
  review: 'Review',
};

const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'fixed_term', 'casual', 'intern', 'temporary', 'seasonal'];
const CONTRACT_TYPES = ['permanent', 'fixed_term', 'specific_task'];
const SCHEDULES = ['full_time', 'part_time'];

const nice = (s: string) => s.replace(/_/g, ' ');

const selectCls =
  'w-full h-9 rounded-md border border-border bg-card px-2 text-sm text-foreground';

export const EngagementWizard: React.FC<Props> = ({ open, employeeId, personName, onClose, onSaved }) => {
  const [step, setStep] = useState<WizardStep>('company');
  const [form, setForm] = useState<WizardForm>(EMPTY_WIZARD);
  const [companies, setCompanies] = useState<EngagementCompany[] | null>(null);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('company');
    setForm(EMPTY_WIZARD);
    setTried(false);
    setCompanies(null);
    setCompaniesError(null);
    engagementsApi
      .companies()
      .then(setCompanies)
      .catch((e) => setCompaniesError(getApiErrorMessage(e, 'Could not load the companies you manage.')));
  }, [open]);

  const errors = useMemo(() => wizardErrors(form, step), [form, step]);
  const set = (patch: Partial<WizardForm>) => setForm((f) => ({ ...f, ...patch }));
  const idx = WIZARD_STEPS.indexOf(step);
  const companyName = companies?.find((c) => c.id === form.legal_entity_id)?.name ?? '';

  const next = () => {
    setTried(true);
    if (Object.keys(errors).length) return;
    setTried(false);
    setStep(WIZARD_STEPS[Math.min(idx + 1, WIZARD_STEPS.length - 1)]);
  };
  const back = () => setStep(WIZARD_STEPS[Math.max(idx - 1, 0)]);

  const save = async (submit: boolean) => {
    setTried(true);
    if (Object.keys(wizardErrors(form, 'review')).length) return;
    setSaving(true);
    try {
      await engagementsApi.create(employeeId, wizardPayload(form, submit));
      toast.success(
        submit
          ? `Engagement with ${companyName} submitted for approval`
          : `Engagement with ${companyName} saved as a draft`,
      );
      await onSaved();
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save the engagement.'), { duration: 7000 });
    } finally {
      setSaving(false);
    }
  };

  const err = (k: string) =>
    tried && errors[k] ? <p className="text-xs text-danger-strong mt-1" role="alert">{errors[k]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add an engagement for {personName}</DialogTitle>
          <DialogDescription>
            Step {idx + 1} of {WIZARD_STEPS.length}: {STEP_TITLE[step]}. Each engagement belongs to one company;
            ending one never ends the others.
          </DialogDescription>
        </DialogHeader>

        {step === 'company' && (
          <div className="space-y-2">
            <Label htmlFor="eng-company">Company</Label>
            {companiesError ? (
              <p className="text-sm text-danger-strong" role="alert">{companiesError}</p>
            ) : companies === null ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading companies…
              </p>
            ) : companies.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                You do not manage engagements in any company. Ask an administrator for company access.
              </p>
            ) : (
              <select
                id="eng-company"
                className={selectCls}
                value={form.legal_entity_id}
                onChange={(e) => set({ legal_entity_id: e.target.value })}
              >
                <option value="">Choose a company…</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            {err('legal_entity_id')}
          </div>
        )}

        {step === 'relationship' && (
          <div className="space-y-4">
            <fieldset>
              <legend className="text-sm font-medium text-foreground">Relationship with {companyName}</legend>
              <RadioGroup
                value={form.relationship_type}
                onValueChange={(v) => set({ relationship_type: v as WizardForm['relationship_type'] })}
                className="mt-2 gap-2"
              >
                {(['employee', 'independent_contractor'] as const).map((r) => (
                  <label key={r} htmlFor={`eng-rel-${r}`} className="flex items-start gap-2 cursor-pointer">
                    <RadioGroupItem id={`eng-rel-${r}`} value={r} aria-label={RELATIONSHIP_LABEL[r]} className="mt-0.5" />
                    <span>
                      <span className="block text-sm text-foreground">{RELATIONSHIP_LABEL[r]}</span>
                      <span className="block text-xs text-muted-foreground">
                        {r === 'employee'
                          ? 'Employed by this company: paid a salary through its payroll, statutory deductions and leave apply.'
                          : 'Not an employee: paid a fee through Contractor & External Pay (withholding tax rules), no payroll or statutory leave.'}
                      </span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
              {err('relationship_type')}
            </fieldset>
            <div>
              <Label htmlFor="eng-start">Start date</Label>
              <Input id="eng-start" type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} />
              {err('start_date')}
            </div>
          </div>
        )}

        {step === 'terms' && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="eng-position">{form.relationship_type === 'employee' ? 'Position' : 'Role / service'}</Label>
              <Input id="eng-position" value={form.position} maxLength={100} onChange={(e) => set({ position: e.target.value })} />
            </div>
            {form.relationship_type === 'employee' && (
              <>
                <div>
                  <Label htmlFor="eng-emptype">Employment type</Label>
                  <select id="eng-emptype" className={selectCls} value={form.employment_type}
                    onChange={(e) => set({ employment_type: e.target.value })}>
                    <option value="">Choose…</option>
                    {EMPLOYMENT_TYPES.map((v) => <option key={v} value={v}>{nice(v)}</option>)}
                  </select>
                  {err('employment_type')}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="eng-contract">Contract type</Label>
                    <select id="eng-contract" className={selectCls} value={form.contract_type}
                      onChange={(e) => set({ contract_type: e.target.value })}>
                      <option value="">Not set</option>
                      {CONTRACT_TYPES.map((v) => <option key={v} value={v}>{nice(v)}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="eng-schedule">Work schedule</Label>
                    <select id="eng-schedule" className={selectCls} value={form.work_schedule}
                      onChange={(e) => set({ work_schedule: e.target.value })}>
                      <option value="">Not set</option>
                      {SCHEDULES.map((v) => <option key={v} value={v}>{nice(v)}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="eng-end">Contract end date (optional)</Label>
              <Input id="eng-end" type="date" value={form.contract_end_date}
                onChange={(e) => set({ contract_end_date: e.target.value })} />
              {err('contract_end_date')}
            </div>
            <p className="text-xs text-muted-foreground">
              {form.relationship_type === 'employee'
                ? 'Salary is set in Payroll for this company after the engagement is approved.'
                : 'The fee is set in Contractor & External Pay for this company after approval.'}
            </p>
          </div>
        )}

        {step === 'review' && (
          <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Person</dt><dd className="col-span-2 text-foreground">{personName}</dd>
            <dt className="text-muted-foreground">Company</dt><dd className="col-span-2 text-foreground">{companyName}</dd>
            <dt className="text-muted-foreground">Relationship</dt>
            <dd className="col-span-2 text-foreground">{form.relationship_type ? RELATIONSHIP_LABEL[form.relationship_type] : ''}</dd>
            <dt className="text-muted-foreground">Starts</dt><dd className="col-span-2 text-foreground">{form.start_date}</dd>
            {form.position && (<><dt className="text-muted-foreground">Position</dt><dd className="col-span-2 text-foreground">{form.position}</dd></>)}
            {form.employment_type && (<><dt className="text-muted-foreground">Type</dt><dd className="col-span-2 text-foreground">{nice(form.employment_type)}</dd></>)}
            {form.contract_type && (<><dt className="text-muted-foreground">Contract</dt><dd className="col-span-2 text-foreground">{nice(form.contract_type)}</dd></>)}
            <dd className="col-span-3 text-xs text-muted-foreground">
              Submitting sends it for approval — someone other than you approves it before it becomes active.
            </dd>
          </dl>
        )}

        <DialogFooter className="gap-2">
          {idx > 0 && <Button variant="outline" onClick={back} disabled={saving}>Back</Button>}
          {step !== 'review' ? (
            <Button onClick={next} disabled={step === 'company' && !companies?.length}>Next</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => void save(false)} disabled={saving}>Save as draft</Button>
              <Button onClick={() => void save(true)} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Submit for approval
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EngagementWizard;
